import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import MathRender from "@/components/Math";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Lightbulb,
  RotateCcw,
  Sparkles,
  X,
  XCircle,
} from "lucide-react";

// ---------- helpers -------------------------------------------------------

// Normalise a maths answer for a forgiving equality check.
// Strips $, spaces, trailing punctuation, leading currency, and lowercases.
// Note: this is a *forgiving* string match — real algebraic equivalence
// comes in a later stage.
function normaliseAnswer(text) {
  if (!text) return "";
  return String(text)
    .replace(/\$/g, "")
    .replace(/\\\s*/g, "")
    .replace(/\s+/g, "")
    .replace(/[.,;!?)(]$/g, "")
    .toLowerCase();
}

function answersMatch(userAns, correctAns) {
  const u = normaliseAnswer(userAns);
  const c = normaliseAnswer(correctAns);
  if (!u || !c) return false;
  if (u === c) return true;
  // Accept partial numeric match — user typed "19" when correct is "$19$"
  const uNum = u.match(/-?[0-9]+(\.[0-9]+)?/g);
  const cNum = c.match(/-?[0-9]+(\.[0-9]+)?/g);
  if (uNum && cNum && uNum.length === 1 && cNum.length === 1) {
    return parseFloat(uNum[0]) === parseFloat(cNum[0]);
  }
  return false;
}

// Class number extracted from `class-11` / `class-10-standard`.
function classLevelFrom(courseId) {
  const m = courseId?.match(/class-(\d+)/);
  return m ? parseInt(m[1], 10) : 10;
}

// ---------- sub-components ------------------------------------------------

function HintRung({ hint }) {
  const kindLabel = {
    nudge: "Nudge",
    method: "Method",
    worked: "Worked step",
    full: "Full solution",
  }[hint.kind] || `Hint ${hint.level}`;
  return (
    <div
      className="fade-up rounded-lg border border-yellow-300/25 bg-yellow-300/[0.04] p-3 text-sm text-zinc-200"
      data-testid={`practice-hint-${hint.level}`}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-yellow-300 mb-1.5">
        <Lightbulb className="w-3 h-3" />
        {kindLabel} · Hint {hint.level}/4
      </div>
      <div className="text-zinc-100 leading-relaxed">
        <MathRender>{hint.text}</MathRender>
      </div>
    </div>
  );
}

function AIPracticeHelp({ question, userAnswer, correctAnswer, classLevel, chapterHint }) {
  const [loading, setLoading] = useState(true);
  const [answer, setAnswer] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/practice-help", {
        question,
        user_answer: userAnswer,
        correct_answer: correctAnswer,
        class_level: classLevel,
        chapter_hint: chapterHint,
      })
      .then((r) => {
        if (!cancelled) setAnswer(r.data.answer || "");
      })
      .catch((err) => {
        if (!cancelled) {
          const detail = err?.response?.data?.detail;
          setError(detail || "AI Tutor is unavailable right now. Try again in a bit.");
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, userAnswer]);

  if (loading) {
    return (
      <div className="fade-up rounded-lg border border-pink-300/25 bg-pink-300/[0.04] p-4 text-sm text-zinc-300" data-testid="practice-ai-loading">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-pink-300 mb-2">
          <Sparkles className="w-3 h-3 animate-pulse" /> AI Tutor is diagnosing your answer…
        </div>
        <div className="animate-pulse text-zinc-500">Analysing the misconception…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="fade-up rounded-lg border border-red-300/30 bg-red-300/5 p-3 text-sm text-red-200" data-testid="practice-ai-error">
        {error}
      </div>
    );
  }
  return (
    <div className="fade-up rounded-lg border border-pink-300/30 bg-gradient-to-br from-pink-300/[0.06] to-transparent p-4" data-testid="practice-ai-response">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-pink-300 mb-2">
        <Sparkles className="w-3 h-3" /> AI Tutor · Misconception coach
      </div>
      <div className="text-sm text-zinc-100 leading-relaxed whitespace-pre-wrap">
        <MathRender>{answer}</MathRender>
      </div>
    </div>
  );
}

// ---------- main page ----------------------------------------------------

export default function LearnSkillPractice() {
  const { skillId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [answer, setAnswer] = useState("");
  const [state, setState] = useState("input"); // input | correct | wrong
  const [hintsShown, setHintsShown] = useState(0);
  const [askedAI, setAskedAI] = useState(false);
  const [tally, setTally] = useState({ correct: 0, wrong: 0, skipped: 0 });
  const startedAtRef = useRef(Date.now());

  useEffect(() => {
    setLoading(true);
    api
      .get(`/learn/skills/${skillId}/practice`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [skillId]);

  const questions = data?.questions || [];
  const q = questions[idx];
  const total = questions.length;
  const chapterHint = data?.unit?.title;
  const classLevel = classLevelFrom(data?.course?.id);
  const isDone = idx >= total && total > 0;

  // Reset per-question state when index changes.
  useEffect(() => {
    setAnswer("");
    setState("input");
    setHintsShown(0);
    setAskedAI(false);
    startedAtRef.current = Date.now();
  }, [idx]);

  const logAttempt = async (isCorrect, userAns, hintCount, aiUsed) => {
    if (!user || !q) return;
    try {
      await api.post(`/learn/skills/${skillId}/attempts`, {
        question_id: q.id,
        is_correct: isCorrect,
        user_answer: userAns || null,
        hints_used: hintCount,
        time_seconds: Math.round((Date.now() - startedAtRef.current) / 1000),
        used_ai_help: aiUsed,
      });
    } catch {
      /* non-fatal */
    }
  };

  const handleCheck = async () => {
    if (!q || !answer.trim()) return;
    const correct = answersMatch(answer, q.answer_text);
    if (correct) {
      setState("correct");
      setTally((t) => ({ ...t, correct: t.correct + 1 }));
      logAttempt(true, answer, hintsShown, askedAI);
    } else {
      setState("wrong");
      // Show first hint automatically so the student always gets a nudge.
      setHintsShown((h) => Math.max(h, 1));
    }
  };

  const revealNextHint = () => setHintsShown((h) => Math.min(h + 1, 4));

  const handleSkip = () => {
    setTally((t) => ({ ...t, skipped: t.skipped + 1 }));
    logAttempt(false, answer || null, hintsShown, askedAI);
    setIdx((i) => i + 1);
  };

  const handleNext = () => {
    // If they marked correct or reviewed the wrong answer, count them out
    if (state === "wrong") {
      setTally((t) => ({ ...t, wrong: t.wrong + 1 }));
      logAttempt(false, answer, hintsShown, askedAI);
    }
    setIdx((i) => i + 1);
  };

  const restart = () => {
    setIdx(0);
    setTally({ correct: 0, wrong: 0, skipped: 0 });
  };

  const progressPct = useMemo(() => {
    if (!total) return 0;
    return Math.round((idx / total) * 100);
  }, [idx, total]);

  if (loading) return <div className="text-zinc-500 text-sm" data-testid="practice-loading">Loading practice…</div>;
  if (!data) return <div className="text-red-300 text-sm">Skill not found.</div>;

  if (!data.practice_bank_ready || total === 0) {
    return (
      <div data-testid="practice-empty" className="max-w-2xl">
        <Link to={`/learn/skill/${skillId}`} className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-yellow-300 mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to skill
        </Link>
        <div className="rounded-lg border border-orange-300/25 bg-orange-300/5 p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-orange-300 mb-1.5 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Practice bank in progress
          </div>
          <div className="text-sm text-zinc-200">
            We're crafting the question set for this skill. Read the lessons in the meantime — they're already live.
          </div>
        </div>
      </div>
    );
  }

  // ---- Session summary ---------------------------------------------------
  if (isDone) {
    const accuracy = total ? Math.round((tally.correct / total) * 100) : 0;
    return (
      <div data-testid="practice-summary" className="max-w-2xl">
        <Link to={`/learn/skill/${skillId}`} className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-yellow-300 mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to skill
        </Link>
        <div className="text-[10px] uppercase tracking-[0.2em] text-pink-300 mb-1">Session complete</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mb-2">
          {accuracy}% on {data.skill.title}
        </h1>
        <p className="text-zinc-400 text-sm mb-6">
          {tally.correct} correct · {tally.wrong} wrong · {tally.skipped} skipped
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={restart}
            data-testid="practice-restart"
            className="rounded-full border border-yellow-300/40 bg-yellow-300/10 hover:bg-yellow-300/20 text-yellow-300 text-sm font-semibold px-4 py-2 flex items-center gap-1.5"
          >
            <RotateCcw className="w-4 h-4" /> Practise again
          </button>
          <button
            onClick={() => navigate(`/learn/unit/${data.unit.id}`)}
            data-testid="practice-back-unit"
            className="rounded-full border border-white/15 hover:border-white/30 text-zinc-200 text-sm font-semibold px-4 py-2"
          >
            More skills in this unit
          </button>
        </div>
      </div>
    );
  }

  // ---- Active question ---------------------------------------------------
  return (
    <div data-testid="practice-page" className="max-w-2xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <Link
          to={`/learn/skill/${skillId}`}
          className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-yellow-300"
          data-testid="practice-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Skill overview
        </Link>
        <div className="text-[11px] font-mono text-zinc-500">
          Q {idx + 1} / {total}
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-6">
        <div className="h-1 rounded-full bg-white/5 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-pink-300 via-orange-300 to-yellow-300 transition-[width] duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Skill title */}
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-pink-300 mb-1">
          {data.course.title} · Unit {data.unit.number}
        </div>
        <h1 className="font-display text-xl md:text-2xl font-bold tracking-tight text-zinc-100">
          {data.skill.title}
        </h1>
      </div>

      {/* Question */}
      <div className="rounded-lg border border-white/10 bg-[#152040] p-5 mb-4" data-testid="practice-stem">
        <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2">Problem</div>
        <div className="text-base text-zinc-100 leading-relaxed">
          <MathRender>{q.stem}</MathRender>
        </div>
      </div>

      {/* Answer input / feedback */}
      {state !== "correct" ? (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 mb-4">
          <label htmlFor="practice-answer" className="block text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1.5">
            Your answer
          </label>
          <div className="flex gap-2">
            <input
              id="practice-answer"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCheck()}
              data-testid="practice-answer-input"
              placeholder="Type your final answer…"
              className="flex-1 bg-transparent border border-white/10 focus:border-yellow-300/60 outline-none rounded-lg px-3 py-2 text-sm text-zinc-100"
              autoComplete="off"
            />
            <button
              onClick={handleCheck}
              disabled={!answer.trim()}
              data-testid="practice-check"
              className="rounded-lg bg-yellow-300 hover:bg-yellow-200 disabled:opacity-40 disabled:cursor-not-allowed text-[#0b1226] text-sm font-semibold px-4 py-2"
            >
              Check
            </button>
          </div>
          {state === "wrong" && (
            <div
              className="mt-3 flex items-center gap-2 text-sm text-red-300"
              data-testid="practice-wrong-banner"
            >
              <XCircle className="w-4 h-4" /> Not quite — try again with the hint below.
            </div>
          )}
          <button
            onClick={handleSkip}
            data-testid="practice-skip"
            className="mt-3 text-[11px] text-zinc-500 hover:text-zinc-300"
          >
            Skip this question →
          </button>
        </div>
      ) : (
        <div
          className="rounded-lg border border-emerald-300/40 bg-emerald-300/10 p-4 mb-4 flex items-start gap-3"
          data-testid="practice-correct-banner"
        >
          <CheckCircle2 className="w-5 h-5 text-emerald-300 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm text-emerald-200 font-semibold mb-0.5">Correct!</div>
            <div className="text-xs text-zinc-400">
              You used {hintsShown} hint{hintsShown === 1 ? "" : "s"}
              {askedAI ? " and the AI Tutor" : ""}.
            </div>
          </div>
          <button
            onClick={handleNext}
            data-testid="practice-next-correct"
            className="rounded-full border border-emerald-300/40 bg-emerald-300/10 hover:bg-emerald-300/20 text-emerald-200 text-xs font-semibold px-3 py-1.5 flex items-center gap-1"
          >
            Next <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Hint ladder */}
      {state !== "correct" && hintsShown > 0 && (
        <div className="space-y-2 mb-4">
          {q.hint_ladder.slice(0, hintsShown).map((h) => (
            <HintRung key={h.level} hint={h} />
          ))}
          {hintsShown < 4 && (
            <button
              onClick={revealNextHint}
              data-testid="practice-more-hint"
              className="text-xs text-yellow-300 hover:text-yellow-200 font-semibold flex items-center gap-1"
            >
              Show next hint <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      {/* AI misconception coach — appears only after a wrong attempt */}
      {state === "wrong" && (
        <div className="mb-4">
          {!askedAI ? (
            <button
              onClick={() => setAskedAI(true)}
              disabled={!user}
              title={!user ? "Sign in to chat with the AI Tutor" : ""}
              data-testid="practice-ask-ai"
              className="w-full flex items-center justify-between gap-2 rounded-lg border border-pink-300/30 bg-pink-300/5 hover:bg-pink-300/10 disabled:opacity-40 disabled:cursor-not-allowed p-3 text-left"
            >
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-pink-300" />
                <div>
                  <div className="text-sm text-zinc-100 font-semibold">Ask the AI Tutor about your answer</div>
                  <div className="text-[11px] text-zinc-500">
                    {user ? "It'll diagnose your specific mistake, not just repeat the solution." : "Sign in to unlock personalised feedback."}
                  </div>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-pink-300" />
            </button>
          ) : (
            <AIPracticeHelp
              question={q.stem}
              userAnswer={answer || "(blank)"}
              correctAnswer={q.answer_text}
              classLevel={classLevel}
              chapterHint={chapterHint}
            />
          )}
        </div>
      )}

      {/* Skip-forward when they've seen the full solution or the AI answer */}
      {state === "wrong" && (hintsShown === 4 || askedAI) && (
        <button
          onClick={handleNext}
          data-testid="practice-next-wrong"
          className="w-full rounded-lg border border-white/15 hover:border-white/30 text-zinc-200 text-sm font-semibold px-4 py-2.5 flex items-center justify-center gap-1.5"
        >
          Got it — next question <ArrowRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
